import { Switch } from '@material-ui/core';
import React, { useState } from 'react';

import {
  ISmartFloorConfigUI,
  ISmartFloorPayloadUI,
  smartFloorConfigCols,
} from '~/src/dash/apiV2/smartFloors/crud';
import { BetaTooltipIcon } from '~/src/dash/components/icons/beta-icon';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { spacing, spacingV2 } from '~/src/dash/components/layout/common';
import ModelMissingPermission from '~/src/dash/components/model-missing-permission';
import { PillSwitcherV2 } from '~/src/dash/components/tabs/PillSwitcherV2';
import {
  borderRadius,
  Col,
  Cols,
  ISimpleOption,
  ISimpleOptionSelect,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  Text,
  TextInput,
  TooltipWithTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  SmartFloorsListContext,
  SmartFloorsLiveAdaptiveContext,
  SmartFloorsLiveContext,
} from '~/src/dash/views/bidders/Bidders/SmartFloors/contexts';
import {
  smartFloorsListViewAdaptiveConfig,
  smartFloorsListViewConfig,
} from '~/src/dash/views/bidders/Bidders/SmartFloors/SmartFloorsListView/config';
import { SmartFloorsListView } from '~/src/dash/views/bidders/Bidders/SmartFloors/SmartFloorsListView/index';
import { smartFloorsLiveAdaptiveConfig } from '~/src/dash/views/bidders/Bidders/SmartFloors/SmartFloorsLiveAdaptiveView/config';
import { SmartFloorsLiveAdaptiveContainer } from '~/src/dash/views/bidders/Bidders/SmartFloors/SmartFloorsLiveAdaptiveView/container';
import { SmartFloorsLive } from '~/src/dash/views/bidders/Bidders/SmartFloors/SmartFloorsLiveView';
import { smartFloorsLiveConfig } from '~/src/dash/views/bidders/Bidders/SmartFloors/SmartFloorsLiveView/config';

interface IProps {
  channelAndGroupMaps: {
    channelMap: Record<string, string>;
    channelGroupMap: Record<string, string>;
  };
  smartFloorConfigs: ISmartFloorConfigUI[];
  selectedSiteIDForSmartFloorsChart: number;
  handleChangeSmartFloorConfig: (
    configIdx: number,
    key: smartFloorConfigCols,
    value: any,
    siteID: number,
    emptyOutSmartFloors: boolean,
    shouldUpdateSmartFloorParams: boolean,
  ) => void;
}

enum ESmartFloorSwitchType {
  Percent = '%',
  Dollar = '$',
}

const incrementTypeCategories: Array<ISimpleOption> = [
  { key: 'percent', value: ESmartFloorSwitchType.Percent, label: ESmartFloorSwitchType.Percent },
  { key: 'dollar', value: ESmartFloorSwitchType.Dollar, label: ESmartFloorSwitchType.Dollar },
];

const getOptions = (
  items: Array<ISimpleOption>,
  selectedValue: string,
  isOptionValid: (value: ISimpleOption) => boolean = () => false,
): Array<ISimpleOptionSelect> => {
  return items.map((item) => ({
    ...item,
    isSelected: item.value === selectedValue,
    isDisabled: !isOptionValid(item),
  }));
};

export const SmartFloors = ({
  channelAndGroupMaps,
  smartFloorConfigs,
  selectedSiteIDForSmartFloorsChart,
  handleChangeSmartFloorConfig,
}: IProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const { user } = useBaseStore();
  const isSuperUser = hasRoleSuperUser(user.user);
  const readAuthorization = IS_AUTHORIZED(['read_bidder'], user.user);
  const writeAuthorization = IS_AUTHORIZED(['write_bidder'], user.user);

  if (!readAuthorization.isAuthorized) {
    return (
      <div>
        <ModelMissingPermission missingPermissions={readAuthorization.missingPermissions} />
      </div>
    );
  }

  const smartFloorConfig = smartFloorConfigs[0];
  let selectedSmartFloor = {} as ISmartFloorPayloadUI;
  let selectedSiteName = '';

  // transform site and channel group IDs to name for table
  smartFloorConfig.SmartFloors.forEach((smartFloor) => {
    smartFloor.ChannelName = channelAndGroupMaps.channelMap[String(smartFloor.SiteID)];

    // selected smartFloorID for chart
    if (smartFloor.SiteID === selectedSiteIDForSmartFloorsChart) {
      selectedSmartFloor = smartFloor;
      selectedSiteName = smartFloor.ChannelName;
    }

    smartFloor.ChannelGroupNames = smartFloor.AssociatedChannelGroupIDs?.map((cgID) => {
      return channelAndGroupMaps.channelGroupMap[String(cgID)];
    });
  });

  // only use testIncrementHighPercentage for now since both high and low % will be the same
  let testIncrementSwitchType = ESmartFloorSwitchType.Dollar;
  let testBidFloorSwitchType = ESmartFloorSwitchType.Dollar;
  if (
    smartFloorConfig.IsTestIncrementHighPercentage ||
    smartFloorConfig.IsTestIncrementLowPercentage
  ) {
    testIncrementSwitchType = ESmartFloorSwitchType.Percent;
  }

  if (smartFloorConfig.IsMaxBidFloorPercentage || smartFloorConfig.IsMinBidFloorPercentage) {
    testBidFloorSwitchType = ESmartFloorSwitchType.Percent;
  }

  const handleSelectSiteOnChart = (siteID: number, currVal: boolean): void => {
    handleChangeSmartFloorConfig(0, 'isSiteIDSelected', currVal, siteID, false, false);
  };

  const handleUpdateBidFloorForSmartFloor = (siteID: number, newValue: number): void => {
    handleChangeSmartFloorConfig(0, 'BaseBidFloor', newValue, siteID, false, true);
  };

  // TODO: THIS IS INCORRECT FORMATTING OF ROWS AND COLS. WE NEED TO FIX THIS. DO NOT COPY THIS PATTERN.
  return (
    <Panels max vertical relative minWidthMain>
      <Panel basePadding largeHorizontalPadding enableSize>
        <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }} smallVerticalPadding>
          <Col style={{ display: 'flex', alignItems: 'center' }}>
            <IconV2 name='shuffle' large />
          </Col>
          <Col>
            <Text small heavy tKey='TestingRange' />
          </Col>
          <Col
            displayFlex
            onClick={() => setIsCollapsed(!isCollapsed)}
            marginLeft={spacing.extraSmall}
          >
            <IconV2
              name='caret'
              large
              style={{ transform: isCollapsed ? 'none' : 'rotate(180deg)', cursor: 'pointer' }}
            />
          </Col>
        </Cols>
        {!isCollapsed && (
          <>
            {isSuperUser && (
              <Cols
                basePadding
                paddingTop='0'
                paddingBottom={spacingV2.small}
                gap={spacingV2.extraSmall}
                style={{ alignItems: 'center' }}
              >
                <Col displayFlex>
                  <BetaTooltipIcon />
                </Col>

                <Col>
                  <Text small bold tKey='AdaptiveFloors' />
                </Col>

                <>
                  <Col>
                    <TooltipWithTranslation tKey='BiddersTooltipSmartFloorsAdaptiveFloors'>
                      <div>
                        <IconV2 small name='info' color={publicaColors.mediumGray} />
                      </div>
                    </TooltipWithTranslation>
                  </Col>

                  <Col displayFlex>
                    <Switch
                      disabled={!isSuperUser}
                      checked={smartFloorConfig.ThompsonSamplingEnabled}
                      name='ThompsonSamplingEnabled'
                      onChange={() => {
                        handleChangeSmartFloorConfig(
                          0,
                          'ThompsonSamplingEnabled',
                          !smartFloorConfig.ThompsonSamplingEnabled,
                          0,
                          false,
                          false,
                        );
                      }}
                    />
                    <Text
                      small
                      style={{ display: 'flex', alignItems: 'center' }}
                      tKey={smartFloorConfig.ThompsonSamplingEnabled ? 'Active' : 'Inactive'}
                    />
                  </Col>
                </>
              </Cols>
            )}

            <Rows
              basePadding
              style={{ background: publicaColors.veryLightGray, borderRadius: borderRadius.large }}
            >
              <Row noPadding marginBottom={spacing.small}>
                <Text extraSmall tKey='BiddersMessageSmartFloorTestingRangeParameters' />
              </Row>
              <Row noVerticalPadding>
                <Cols gap={spacingV2.medium} style={{ alignItems: 'center' }}>
                  <Col style={{ minWidth: '360px' }}>
                    <Text small bold tKey='ABTestingTraffic' />
                    <Cols style={{ alignItems: 'center' }}>
                      <Col>
                        <Text
                          mini
                          style={{ backgroundColor: publicaColors.skyBlue }}
                          tKey='BiddersMessageSmartFloorsABTestingTraffic'
                        />
                      </Col>
                      <Col displayFlex style={{ marginLeft: spacing.extraSmall }}>
                        <TooltipWithTranslation tKey='BiddersTooltipSmartFloorsABTestingTraffic'>
                          <div>
                            <IconV2 small name='info' color={publicaColors.mediumGray} />
                          </div>
                        </TooltipWithTranslation>
                      </Col>
                    </Cols>
                  </Col>

                  <Col displayFlex>
                    <Switch
                      disabled={!writeAuthorization.isAuthorized}
                      checked={smartFloorConfig.IsABTestEnabled}
                      name='Active'
                      onChange={() => {
                        handleChangeSmartFloorConfig(
                          0,
                          'IsABTestEnabled',
                          !smartFloorConfig.IsABTestEnabled,
                          0,
                          false,
                          false,
                        );
                      }}
                    />
                    <Text
                      small
                      style={{ display: 'flex', alignItems: 'center' }}
                      tKey={smartFloorConfig.IsABTestEnabled ? 'Active' : 'Inactive'}
                    />
                  </Col>
                </Cols>
              </Row>

              <Row extraSmallVerticalPadding>
                <Cols gap={spacingV2.extraLarge} style={{ alignItems: 'center' }}>
                  <Col style={{ minWidth: '360px' }}>
                    <Text small bold tKey='Increment' />
                    <Text
                      mini
                      style={{ backgroundColor: publicaColors.skyBlue, width: 'fit-content' }}
                      tKey='BiddersMessageSmartFloorsIncrement'
                    />
                  </Col>

                  <Col>
                    <Cols wrap style={{ alignItems: 'center' }}>
                      <Col style={{ marginRight: spacing.base, marginTop: spacing.small }}>
                        <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                          <Col>
                            <Text small bold tKey='Unit' />
                          </Col>
                          <Col>
                            <PillSwitcherV2
                              size='extraSmall'
                              options={getOptions(incrementTypeCategories, testIncrementSwitchType)}
                              backgroundColor={publicaColors.whiteOrchid}
                              onChange={(type: ESmartFloorSwitchType) => {
                                handleChangeSmartFloorConfig(
                                  0,
                                  'IsTestIncrementHighPercentage',
                                  type === ESmartFloorSwitchType.Percent,
                                  0,
                                  false,
                                  true,
                                );
                              }}
                            />
                          </Col>
                        </Cols>
                      </Col>

                      <Col style={{ marginTop: spacing.small }}>
                        <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                          <Col>
                            <Text small bold tKey='Value' />
                          </Col>
                          <Col>
                            <TextInput
                              type='number'
                              min={0}
                              initialValue={String(smartFloorConfig.TestIncrementHigh)}
                              width='5rem'
                              inputSize='small'
                              step={0.5}
                              placeholder={`${testIncrementSwitchType} inc`}
                              handleOnChange={(input) => {
                                let numInput = parseFloat(input);
                                if (isNaN(numInput)) {
                                  numInput = 0;
                                }
                                handleChangeSmartFloorConfig(
                                  0,
                                  'TestIncrementHigh',
                                  numInput,
                                  0,
                                  false,
                                  true,
                                );
                              }}
                            />
                          </Col>
                        </Cols>
                      </Col>
                    </Cols>
                  </Col>
                </Cols>
              </Row>

              <Row extraSmallVerticalPadding>
                <Cols gap={spacingV2.extraLarge}>
                  <Col style={{ minWidth: '360px' }}>
                    <Text small bold tKey='FloorLimits' />
                    <Text
                      mini
                      style={{ backgroundColor: publicaColors.skyBlue, width: 'fit-content' }}
                      tKey='BiddersMessageSmartFloorsFloorLimits'
                    />
                  </Col>

                  <Col>
                    <Cols wrap gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                      <Col style={{ marginRight: spacing.base, marginTop: spacing.small }}>
                        <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                          <Col>
                            <Text small bold tKey='Unit' />
                          </Col>
                          <Col>
                            <PillSwitcherV2
                              size='extraSmall'
                              options={getOptions(incrementTypeCategories, testBidFloorSwitchType)}
                              backgroundColor={publicaColors.whiteOrchid}
                              onChange={(type: ESmartFloorSwitchType) => {
                                handleChangeSmartFloorConfig(
                                  0,
                                  'IsMaxBidFloorPercentage', // isMinBidFloorPercentage uses the same switch for now
                                  type === ESmartFloorSwitchType.Percent,
                                  0,
                                  false,
                                  true,
                                );
                              }}
                            />
                          </Col>
                        </Cols>
                      </Col>

                      <Col style={{ marginRight: spacing.base, marginTop: spacing.small }}>
                        <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                          <Col>
                            <Text small bold tKey='MinValue' />
                          </Col>
                          <Col>
                            <TextInput
                              type='number'
                              min={0}
                              initialValue={String(smartFloorConfig.MinBidFloor)}
                              width='5rem'
                              inputSize='small'
                              step={0.5}
                              placeholder={`${testBidFloorSwitchType} inc`}
                              handleOnChange={(input) => {
                                let numInput = parseFloat(input);
                                if (isNaN(numInput)) {
                                  numInput = 0;
                                }
                                handleChangeSmartFloorConfig(
                                  0,
                                  'MinBidFloor',
                                  numInput,
                                  0,
                                  false,
                                  true,
                                );
                              }}
                            />
                          </Col>
                        </Cols>
                      </Col>

                      <Col style={{ marginTop: spacing.small }}>
                        <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                          <Col>
                            <Text small bold tKey='MaxValue' />
                          </Col>
                          <Col>
                            <TextInput
                              type='number'
                              min={0}
                              initialValue={String(smartFloorConfig.MaxBidFloor)}
                              width='5rem'
                              inputSize='small'
                              step={0.5}
                              placeholder={`${testBidFloorSwitchType} inc`}
                              handleOnChange={(input) => {
                                let numInput = parseFloat(input);
                                if (isNaN(numInput)) {
                                  numInput = 0;
                                }
                                handleChangeSmartFloorConfig(
                                  0,
                                  'MaxBidFloor',
                                  numInput,
                                  0,
                                  false,
                                  true,
                                );
                              }}
                            />
                          </Col>
                        </Cols>
                      </Col>
                    </Cols>
                  </Col>
                </Cols>
              </Row>
            </Rows>
          </>
        )}
      </Panel>

      <Panel largeHorizontalPadding noVerticalPadding enableSize>
        {smartFloorConfig.ThompsonSamplingEnabled ? (
          <SmartFloorsLiveAdaptiveContext.Provider
            value={{
              liveConfig: smartFloorsLiveAdaptiveConfig,
              selectedSite: { ID: selectedSiteIDForSmartFloorsChart, Name: selectedSiteName },
              selectedSmartFloor,
            }}
          >
            <SmartFloorsLiveAdaptiveContainer />
          </SmartFloorsLiveAdaptiveContext.Provider>
        ) : (
          <SmartFloorsLiveContext.Provider
            value={{
              SmartFloorsStore: {
                Live: smartFloorsLiveConfig,
                selectedSmartFloor,
                selectedSiteName,
                selectedSiteID: selectedSiteIDForSmartFloorsChart,
              },
            }}
          >
            <SmartFloorsLive />
          </SmartFloorsLiveContext.Provider>
        )}
      </Panel>

      <Panel basePadding largeHorizontalPadding enableSize>
        <SmartFloorsListContext.Provider
          value={{
            SmartFloors: {
              data: smartFloorConfig.SmartFloors,
              cfg: smartFloorConfig.ThompsonSamplingEnabled
                ? smartFloorsListViewAdaptiveConfig
                : smartFloorsListViewConfig,
              selectedSiteIDForSmartFloorsChart,
            },
            handlers: {
              handleSelectSiteOnChart,
              handleUpdateBidFloorForSmartFloor,
            },
            shouldAllowSortingTable: smartFloorConfig.shouldAllowSortingTable || false,
            readAuthorization,
            writeAuthorization,
          }}
        >
          <SmartFloorsListView />
        </SmartFloorsListContext.Provider>
      </Panel>
      <PanelBreak height={spacing.large} />
    </Panels>
  );
};
